/**
 * OMR Card Geometry Calculations
 * Helper functions for positioning OMR elements
 */

import {
  PAGE_WIDTH_MM,
  PAGE_HEIGHT_MM,
  PAGE_MARGIN_MM,
  USABLE_WIDTH_MM,
  BUBBLE_DIAMETER_MM,
  BUBBLE_SPACING_MM,
  ID_BUBBLE_DIAMETER_MM,
  ID_BUBBLE_SPACING_VERTICAL_MM,
  ID_BUBBLE_SPACING_HORIZONTAL_MM,
  STUDENT_ID_DIGITS,
  STUDENT_ID_OPTIONS,
  QUESTIONS_PER_COLUMN,
  QUESTION_LINE_HEIGHT_MM,
  TIMING_MARK_DISTANCE_FROM_EDGE_MM,
  TIMING_MARK_WIDTH_MM,
  ALTERNATIVES,
  mmToPx,
} from './constants';

/**
 * Calculate the horizontal center position for centered elements
 */
export const getCenterX = (elementWidthMm) => {
  return (USABLE_WIDTH_MM - elementWidthMm) / 2 + PAGE_MARGIN_MM;
};

/**
 * Calculate positions for the 5×10 matrícula (student ID) grid
 * Returns array of bubble positions [{ digit, option, x, y }]
 */
export const getStudentIdGridPositions = (startYMm) => {
  const positions = [];

  // Calculate total grid dimensions
  const gridWidth =
    (ID_BUBBLE_DIAMETER_MM * STUDENT_ID_DIGITS) +
    (ID_BUBBLE_SPACING_HORIZONTAL_MM * (STUDENT_ID_DIGITS - 1));

  const gridHeight =
    (ID_BUBBLE_DIAMETER_MM * STUDENT_ID_OPTIONS) +
    (ID_BUBBLE_SPACING_VERTICAL_MM * (STUDENT_ID_OPTIONS - 1));

  const startXMm = getCenterX(gridWidth);

  for (let digit = 0; digit < STUDENT_ID_DIGITS; digit++) {
    for (let option = 0; option < STUDENT_ID_OPTIONS; option++) {
      positions.push({
        digit,
        option,
        x: startXMm + (digit * (ID_BUBBLE_DIAMETER_MM + ID_BUBBLE_SPACING_HORIZONTAL_MM)),
        y: startYMm + (option * (ID_BUBBLE_DIAMETER_MM + ID_BUBBLE_SPACING_VERTICAL_MM)),
        diameter: ID_BUBBLE_DIAMETER_MM,
      });
    }
  }

  return positions;
};

/**
 * Calculate positions for question alternatives (A, B, C, D, E)
 * Returns array of bubble positions [{ alternative, x, y }]
 */
export const getAlternativesPositions = (questionLineYMm) => {
  const positions = [];

  // Calculate total width of all bubbles
  const totalWidth =
    (BUBBLE_DIAMETER_MM * ALTERNATIVES.length) +
    (BUBBLE_SPACING_MM * (ALTERNATIVES.length - 1));

  const startXMm = getCenterX(totalWidth);

  ALTERNATIVES.forEach((alt, index) => {
    positions.push({
      alternative: alt,
      x: startXMm + (index * (BUBBLE_DIAMETER_MM + BUBBLE_SPACING_MM)),
      y: questionLineYMm,
      diameter: BUBBLE_DIAMETER_MM,
    });
  });

  return positions;
};

/**
 * Calculate question layout for 2-column format
 * Returns array of questions with their positions
 */
export const getQuestionsLayout = (questions, startYMm) => {
  const layout = [];
  const columnWidth = (USABLE_WIDTH_MM - 10) / 2; // 10mm space between columns

  questions.forEach((question, index) => {
    const columnNumber = Math.floor(index / QUESTIONS_PER_COLUMN); // 0 or 1
    const rowInColumn = index % QUESTIONS_PER_COLUMN;

    const columnXOffset = columnNumber * (columnWidth + 10);
    const yOffset = rowInColumn * QUESTION_LINE_HEIGHT_MM;

    layout.push({
      number: index + 1,
      question,
      columnNumber,
      rowInColumn,
      x: PAGE_MARGIN_MM + columnXOffset,
      y: startYMm + yOffset,
      height: QUESTION_LINE_HEIGHT_MM,
    });
  });

  return layout;
};

/**
 * Calculate timing mark positions (left and right margins)
 */
export const getTimingMarkPositions = (questionLineYMm, numberOfQuestions) => {
  const leftPositions = [];
  const rightPositions = [];

  for (let i = 0; i < numberOfQuestions; i++) {
    const yMm = questionLineYMm + (i * QUESTION_LINE_HEIGHT_MM);

    leftPositions.push({
      x: TIMING_MARK_DISTANCE_FROM_EDGE_MM,
      y: yMm,
    });

    rightPositions.push({
      x: PAGE_WIDTH_MM - TIMING_MARK_DISTANCE_FROM_EDGE_MM - TIMING_MARK_WIDTH_MM,
      y: yMm,
    });
  }

  return { left: leftPositions, right: rightPositions };
};

/**
 * Validate that layout fits within page bounds
 */
export const validateLayout = (numberOfQuestions, startYMm) => {
  const questionsEndY = startYMm + (numberOfQuestions * QUESTION_LINE_HEIGHT_MM);
  const pageEndY = PAGE_HEIGHT_MM - PAGE_MARGIN_MM;

  if (questionsEndY > pageEndY) {
    console.warn(
      `Layout warning: Questions extend beyond page bounds. ` +
      `End Y: ${questionsEndY.toFixed(1)}mm, Page end: ${pageEndY}mm`
    );
    return false;
  }

  return true;
};
